import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, Badge, Button, Skeleton } from "@learn-me/ui";
import { adminApi, type Invitation } from "@/features/admin/api/adminApi";
import { formatDate } from "@learn-me/utils";
import toast from "react-hot-toast";
import {
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  RotateCcw,
  Trash2,
  BookOpen,
  Package,
} from "lucide-react";

function getStatusBadge(status: Invitation["status"]) {
  switch (status) {
    case "PENDING":
      return (
        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" />
          Pendiente
        </Badge>
      );
    case "ACCEPTED":
      return (
        <Badge variant="default" className="bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Aceptada
        </Badge>
      );
    case "EXPIRED":
      return (
        <Badge variant="secondary" className="bg-gray-100 text-gray-800">
          <Clock className="h-3 w-3 mr-1" />
          Expirada
        </Badge>
      );
    case "CANCELLED":
      return (
        <Badge variant="secondary" className="bg-red-100 text-red-800">
          <XCircle className="h-3 w-3 mr-1" />
          Cancelada
        </Badge>
      );
  }
}

export function InvitationsList() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-invitations"],
    queryFn: () => adminApi.getInvitations(1, 50),
  });

  const resendMutation = useMutation({
    mutationFn: adminApi.resendInvitation,
    onSuccess: () => {
      toast.success("Invitacion reenviada exitosamente");
      queryClient.invalidateQueries({ queryKey: ["admin-invitations"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al reenviar la invitacion");
    },
  });

  const cancelMutation = useMutation({
    mutationFn: adminApi.cancelInvitation,
    onSuccess: () => {
      toast.success("Invitacion cancelada");
      queryClient.invalidateQueries({ queryKey: ["admin-invitations"] });
      queryClient.invalidateQueries({ queryKey: ["admin-invitation-stats"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al cancelar la invitacion");
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-16 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!data?.invitations?.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No hay invitaciones</h3>
          <p className="text-muted-foreground">
            Usa el boton &quot;Invitar Estudiante&quot; para enviar
            invitaciones.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {data.invitations.map((invitation) => (
        <Card key={invitation.id}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{invitation.email}</span>
                {getStatusBadge(invitation.status)}
              </div>

              {(invitation.firstName || invitation.lastName) && (
                <p className="text-sm text-muted-foreground mt-1">
                  {invitation.firstName} {invitation.lastName}
                </p>
              )}

              <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
                <span>
                  Enviada: {formatDate(new Date(invitation.createdAt))}
                </span>
                {invitation.status === "PENDING" && (
                  <span>
                    Expira: {formatDate(new Date(invitation.expiresAt))}
                  </span>
                )}
                {invitation.acceptedAt && (
                  <span>
                    Aceptada: {formatDate(new Date(invitation.acceptedAt))}
                  </span>
                )}
              </div>

              {(invitation.autoEnrollCourse || invitation.autoEnrollBundle) && (
                <div className="flex items-center gap-2 mt-2">
                  {invitation.autoEnrollCourse && (
                    <Badge variant="outline" className="text-xs">
                      <BookOpen className="h-3 w-3 mr-1" />
                      {invitation.autoEnrollCourse.title}
                    </Badge>
                  )}
                  {invitation.autoEnrollBundle && (
                    <Badge variant="outline" className="text-xs">
                      <Package className="h-3 w-3 mr-1" />
                      {invitation.autoEnrollBundle.name}
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {invitation.status === "PENDING" && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => resendMutation.mutate(invitation.id)}
                  disabled={resendMutation.isPending}
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Reenviar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (confirm("¿Cancelar esta invitacion?")) {
                      cancelMutation.mutate(invitation.id);
                    }
                  }}
                  disabled={cancelMutation.isPending}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
